<template>
  <div v-if="canExport" class="inline-block">
    <!-- Single export: render as a plain button -->
    <button
      v-if="visibleExports.length === 1"
      type="button"
      :class="btnClass"
      :disabled="disabled || visibleExports[0].loading"
      @click="handleItemClick(visibleExports[0])"
    >
      <FontAwesomeIcon
        v-if="visibleExports[0].loading"
        :icon="['fas', 'spinner']"
        spin
        :class="iconClass"
      />
      <FontAwesomeIcon
        v-else
        :icon="visibleExports[0].icon"
        :class="iconClass"
      />
      {{ visibleExports[0].loading ? 'Exporting...' : visibleExports[0].label }}
    </button>

    <!-- Multiple exports: render as a dropdown -->
    <template v-else>
      <button
        ref="triggerButton"
        type="button"
        :class="btnClass"
        :disabled="disabled || anyLoading"
        @click="toggleMenu"
      >
        <FontAwesomeIcon
          v-if="anyLoading"
          :icon="['fas', 'spinner']"
          spin
          :class="iconClass"
        />
        <FontAwesomeIcon
          v-else
          :icon="['fas', 'download']"
          :class="iconClass"
        />
        {{ anyLoading ? 'Exporting...' : 'Export' }}
        <FontAwesomeIcon
          :icon="['fas', 'chevron-down']"
          :class="[chevronClass, { 'rotate-180': isOpen }]"
        />
      </button>

      <Teleport to="body">
        <Transition>
          <div
            v-if="isOpen"
            ref="menuContent"
            :style="positionStyles"
            class="z-50"
          >
            <div class="py-1 bg-white rounded-md shadow-lg border border-gray-200 min-w-[5rem]">
              <button
                v-for="(exp, index) in visibleExports"
                :key="index"
                type="button"
                :class="menuItemClass"
                :disabled="exp.loading"
                @click="handleItemClick(exp)"
              >
                <FontAwesomeIcon
                  v-if="exp.loading"
                  :icon="['fas', 'spinner']"
                  spin
                  :class="iconClass"
                />
                <FontAwesomeIcon
                  v-else
                  :icon="exp.icon"
                  :class="iconClass"
                />
                {{ exp.loading ? 'Exporting...' : exp.menuLabel }}
              </button>
            </div>
          </div>
        </Transition>
      </Teleport>
    </template>
  </div>
</template>

<script>
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { library } from '@fortawesome/fontawesome-svg-core';
import {
  faFileExcel,
  faFilePdf,
  faFileWord,
  faFilePowerpoint,
  faDownload,
  faSpinner,
  faChevronDown,
} from '@fortawesome/free-solid-svg-icons';
import { computePosition, offset, flip, shift } from '@floating-ui/dom';
import { FEATURES } from '@/constants/features';
import { canAccessFeature } from '@/utils/access';

library.add(
  faFilePdf,
  faFileWord,
  faFileWord,
  faFileExcel,
  faFilePowerpoint,
  faDownload,
  faSpinner,
  faChevronDown,
);

const TYPE_CONFIG = {
  csv: { label: 'Export CSV', menuLabel: 'CSV', icon: ['fas', 'file-excel'] },
  excel: { label: 'Export Excel', menuLabel: 'Excel', icon: ['fas', 'file-excel'] },
  pdf: { label: 'Export PDF', menuLabel: 'PDF', icon: ['fas', 'file-pdf'] },
  docx: { label: 'Export Word', menuLabel: 'Word', icon: ['fas', 'file-word'] },
  pptx: { label: 'Export PPTX', menuLabel: 'PPTX', icon: ['fas', 'file-powerpoint'] },
  chart: { label: '', menuLabel: 'Chart', icon: ['fas', 'download'] },
};

export default {
  name: 'ExportMenu',
  components: { FontAwesomeIcon },
  props: {
    exports: {
      type: Array,
      required: true,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    // sm and lg are for backwards compatibility with existing button sizes; prefer md for new usage
    size: {
      type: String,
      default: 'md',
      validator: (v) => ['sm', 'md', 'lg'].includes(v),
    },
  },
  data() {
    return {
      isOpen: false,
      positionStyles: {
        position: 'absolute',
        top: '0',
        left: '0',
      },
    };
  },
  computed: {
    canExport() {
      return canAccessFeature(FEATURES.ACTION_EXPORT);
    },
    visibleExports() {
      return this.exports.map((exp) => {
        const config = TYPE_CONFIG[exp.type] ?? {};
        const menuBase = config.menuLabel ?? 'Export';
        return {
          ...exp,
          label: exp.label ?? config.label ?? 'Export',
          menuLabel: exp.menuLabel ?? (exp.label ? `${menuBase} (${exp.label})` : menuBase),
          icon: config.icon ?? ['fas', 'download'],
          loading: exp.loading ?? false,
        };
      });
    },
    anyLoading() {
      return this.visibleExports.some((e) => e.loading);
    },
    btnClass() {
      const base = 'inline-flex items-center bg-white text-gray-600 hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed border border-gray-300';
      if (this.size === 'sm') return `${base} gap-1 px-2.5 py-1 text-[11.5px] rounded-[6px]`;
      if (this.size === 'lg') return `${base} gap-2 px-4 py-1.5 text-sm rounded-lg`;
      return `${base} gap-1.5 px-2 py-1.5 text-xs rounded-md`;
    },
    iconClass() {
      if (this.size === 'sm') return 'w-[11px] h-[11px]';
      if (this.size === 'lg') return 'w-3.5 h-3.5';
      return 'w-3 h-3';
    },
    chevronClass() {
      const base = 'ml-auto transition-transform duration-200';
      if (this.size === 'sm') return `w-[9px] h-[9px] ${base}`;
      if (this.size === 'lg') return `w-3 h-3 ${base}`;
      return `w-2.5 h-2.5 ${base}`;
    },
    menuItemClass() {
      const base = 'w-full text-left text-gray-600 hover:bg-gray-50 focus:outline-none flex items-center transition-colors disabled:opacity-50 disabled:cursor-not-allowed';
      if (this.size === 'sm') return `${base} px-2.5 py-1 text-[11.5px] gap-1`;
      if (this.size === 'lg') return `${base} px-4 py-2 text-sm gap-2`;
      return `${base} px-3 py-1.5 text-xs gap-1.5`;
    },
  },
  mounted() {
    // single-button mode has no menu to close, so it needs no global listeners
    if (this.visibleExports.length > 1) {
      document.addEventListener('click', this.handleClickOutside);
      document.addEventListener('keydown', this.handleKeydown);
      // capture phase so scrolling any ancestor container, not just the page,
      // re-anchors the teleported menu to the trigger (same as MultiSelectDropdown)
      window.addEventListener('scroll', this.updatePosition, true);
      window.addEventListener('resize', this.updatePosition);
    }
  },
  beforeUnmount() {
    document.removeEventListener('click', this.handleClickOutside);
    document.removeEventListener('keydown', this.handleKeydown);
    window.removeEventListener('scroll', this.updatePosition, true);
    window.removeEventListener('resize', this.updatePosition);
  },
  methods: {
    toggleMenu() {
      this.isOpen = !this.isOpen;
      if (this.isOpen) {
        this.$nextTick(() => this.updatePosition());
      }
    },
    closeMenu() {
      this.isOpen = false;
    },
    handleItemClick(exp) {
      this.closeMenu();
      exp.action();
    },
    handleClickOutside(e) {
      if (
        !this.$refs.triggerButton?.contains(e.target) &&
        !this.$refs.menuContent?.contains(e.target)
      ) {
        this.closeMenu();
      }
    },
    handleKeydown(e) {
      if (e.key === 'Escape' && this.isOpen) {
        this.closeMenu();
      }
    },
    updatePosition() {
      if (!this.$refs.triggerButton || !this.$refs.menuContent) return;
      computePosition(this.$refs.triggerButton, this.$refs.menuContent, {
        placement: 'bottom-end',
        middleware: [offset(5), flip(), shift()],
      }).then(({ x, y }) => {
        this.positionStyles = {
          position: 'absolute',
          top: `${y}px`,
          left: `${x}px`,
        };
      });
    },
  },
};
</script>

<style>
.v-enter-active,
.v-leave-active {
  transition: opacity 0.2s ease;
}
.v-enter-from,
.v-leave-to {
  opacity: 0;
}
</style>
